/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTenantForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing tenant form state and input changes.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo } from 'react';
import type {
  TenantFormState,
  DomainFormState,
  UserFormState
} from './useTenantData';

const generateSlug = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export function useTenantForm(
  formData: TenantFormState,
  setFormData: React.Dispatch<React.SetStateAction<TenantFormState>>,
  domainData: DomainFormState,
  setDomainData: React.Dispatch<React.SetStateAction<DomainFormState>>,
  userData: UserFormState,
  setUserData: React.Dispatch<React.SetStateAction<UserFormState>>,
  originalData: TenantFormState,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) {
  // Check for changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Handle tenant input changes
  const handleInputChange = useCallback((field: string, value: unknown) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Auto-generate slug when name changes
      if (field === 'name' && value) {
        newData.slug = generateSlug(String(value));
      }

      return newData;
    });

    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, [setFormData, setErrors]);

  // Handle domain input changes
  const handleDomainInputChange = useCallback((field: keyof DomainFormState, value: unknown) => {
    setDomainData(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setDomainData]);

  // Handle user input changes
  const handleUserInputChange = useCallback((field: keyof UserFormState, value: unknown) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setUserData]);

  return {
    hasChanges,
    handleInputChange,
    handleDomainInputChange,
    handleUserInputChange
  };
}

